import { type JSX, ReactNode } from "react";

import Card, { CardProps } from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import {
  ThumbnailActionArea,
  ThumbnailValueComponentsProvider,
} from "@plansoft-configuration-renderer/components";
import {
  useCsAttribute,
  useCsValue,
  useLocalOptionSelected,
} from "@plansoft-configuration-renderer/framework";

export interface ThumbnailItemProps {
  children: ReactNode;
}

interface MuiThumbnailItemProps extends CardProps {
  disabled?: boolean;
}

/**
 * Wrapper for attributes with support for theming
 */
const MuiThumbnailItemCard = styled(Card, {
  name: "MuiThumbnailItemCard",
  slot: "root",
  shouldForwardProp: (prop) => prop !== "disabled",
})<MuiThumbnailItemProps>(({ disabled }) => ({
  ...(disabled && {
    pointerEvents: "none",
    opacity: 0.75,
  }),
}));

/**
 * Renders basic wrapper around the content
 */
export function ThumbnailItem(props: ThumbnailItemProps): JSX.Element {
  // there is a isSelected value but it does not work with local values
  const isSelected = useLocalOptionSelected();

  const attribute = useCsAttribute();
  const value = useCsValue();

  const isAttributeDisabled = !attribute.isAllowed;
  const isValueDisabled = !value.isAllowed || !value.isActive;
  const cardDisabled = !isAttributeDisabled && isValueDisabled;

  // Note: do not change "elevation" property of Card/Paper since it interferes with "raised"
  return (
    <MuiThumbnailItemCard raised={isSelected} disabled={cardDisabled}>
      <ThumbnailValueComponentsProvider>
        <ThumbnailActionArea>{props.children}</ThumbnailActionArea>
      </ThumbnailValueComponentsProvider>
    </MuiThumbnailItemCard>
  );
}
